
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface EvangelismResponse {
  response: string;
  converted?: boolean;
}

const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

export async function sendMessage(
  worldview: string,
  message: string,
  conversationHistory: Array<{ sender: "user" | "ai"; text: string }>,
  difficulty: string = "intermediate"
): Promise<EvangelismResponse> {
  let retries = 0;
  
  // Send full conversation history for intermediate mode (like beginner mode)
  // Only limit for very long conversations to prevent API limits
  const limitedHistory = conversationHistory.length > 20 ? conversationHistory.slice(-15) : conversationHistory;
  
  while (retries <= MAX_RETRIES) {
    try {
      console.log(`Sending message to evangelism-chat function (attempt ${retries + 1})`);
      
      const { data, error } = await supabase.functions.invoke('evangelism-chat', {
        body: {
          faith_group: worldview,
          question: message,
          conversation_history: limitedHistory.map(msg => ({
            sender: msg.sender,
            text: msg.text
          })),
          difficulty
        },
      });
      
      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(`Error: ${error.message}`);
      }

      // Log success for debugging
      console.log(`Successfully received response from evangelism chat service for ${worldview}`);
      
      if (!data) {
        throw new Error('Received empty response from server');
      }
      
      // Enhanced logging for debugging conversion status
      console.log(`Response data:`, data);
      console.log(`Conversion detected:`, data.converted);
      
      return data as EvangelismResponse;
    } catch (error) {
      console.error(`Error fetching response (attempt ${retries + 1}):`, error);
      
      if (retries < MAX_RETRIES) {
        // Wait before retrying
        console.log(`Retrying in ${RETRY_DELAY}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retries + 1)));
        retries++;
      } else {
        // All retries failed
        console.error("All retry attempts failed");
        
        let errorMessage = "There was a problem connecting to the evangelism chat service.";
        
        if (error instanceof Error) {
          if (error.message.includes("Failed to fetch") || error.message.includes("NetworkError")) {
            errorMessage = "Network connection error. Please check your internet connection and try again.";
          } else if (error.message.includes("timeout")) {
            errorMessage = "The request timed out. Please try again later.";
          } else if (error.message.includes("rate limit") || error.message.includes("quota exceeded")) {
            errorMessage = "Service is currently experiencing high demand. Please try again in a few minutes.";
          }
        }
        
        toast({
          title: "Connection Error",
          description: errorMessage,
          variant: "destructive",
        });
        
        throw new Error(errorMessage);
      }
    }
  }
  
  // This should never be reached due to the throw in the final retry failure
  throw new Error("Failed to send message after multiple attempts");
}
